import Typesense from 'typesense';
import {
  SearchParams,
  SearchParamsWithPreset,
} from 'typesense/lib/Typesense/Documents';
import {
  formatTypesenseToLaravelPagination,
  formatFilterParams,
} from './typesense.utils';
import { NSFW_TAGS } from '@/lib/constants/nsfw-tags';
import { nsfwSettingsAtom } from '@/lib/hooks/use-nsfw-settings';
import { getDefaultStore } from 'jotai';

const isSafeVersion = process.env.NEXT_PUBLIC_IS_SAFE === 'true';

const searchClient = new Typesense.Client({
  nodes: [
    {
      host: process.env.NEXT_PUBLIC_TYPESENSE_HOST!,
      port: parseInt(process.env.NEXT_PUBLIC_TYPESENSE_PORT!, 10),
      protocol: process.env.NEXT_PUBLIC_TYPESENSE_PROTOCOL!,
    },
  ],
  apiKey: process.env.NEXT_PUBLIC_TYPESENSE_API_KEY!,
  nearestNode: {
    host: process.env.NEXT_PUBLIC_TYPESENSE_HOST!,
    port: parseInt(process.env.NEXT_PUBLIC_TYPESENSE_PORT!, 10),
    protocol: process.env.NEXT_PUBLIC_TYPESENSE_PROTOCOL!,
  },
  numRetries: 8,
  connectionTimeoutSeconds: 2,
});

// Helper function to get NSFW filter based on user settings
function getNSFWFilter() {
  const store = getDefaultStore();
  const nsfwSettings = store.get(nsfwSettingsAtom);
  const isSafeVersion = process.env.NEXT_PUBLIC_IS_SAFE === 'true';

  const tagsToExclude = [];

  // In safe version, always exclude all NSFW content
  if (isSafeVersion) {
    tagsToExclude.push(...NSFW_TAGS.explicit, ...NSFW_TAGS.mature);
  } else {
    // Add tags to exclude based on user settings
    if (!nsfwSettings.explicit) {
      tagsToExclude.push(...NSFW_TAGS.explicit);
    }

    if (!nsfwSettings.mature) {
      tagsToExclude.push(...NSFW_TAGS.mature);
    }
  }

  // If there are tags to exclude, create the filter
  if (tagsToExclude.length > 0) {
    return `tag_slugs:!=[${tagsToExclude.join(', ')}]`;
  }

  return null;
}

export function searchProducts(param: any): Promise<any> {
  const {
    name: query,
    orderBy,
    sortedBy,
    limit,
    exclude: excludeId,
    page,
    filter_by,
  } = param;
  const currentPage = page || 1;

  let searchParameters: SearchParams | SearchParamsWithPreset = {
    q: query || '*',
    query_by: 'name,description,category_slugs,tag_slugs,embedding',
    // 'name,description,prompt_example_1,prompt_example_2,prompt_example_3,prompt_example_4,embedding',
    // TODO: investigate bug if * is used with sort_by vector distance:
    // sort_by vector_distance is only supported for vector queries, semantic search and hybrid search.
    // query_by_weights: '5,1,1,1,1,1,1',  // Heavily prioritize 'name'
    query_by_weights: '5,1,1,1,1', // Heavily prioritize 'name'
    sort_by:
      orderBy && sortedBy
        ? `_text_match:desc,${
            query ? '_vector_distance:asc,' : ''
          }${orderBy}:${sortedBy}`
        : `_text_match:desc,${query ? '_vector_distance:asc,' : ''}`, // Default to sorting by text match
    filter_by,
    per_page: limit || 12,
    vector_query: 'embedding:([], alpha: 0.3)',
    prefix: false,
    page: currentPage,
  };

  // Exclude a specific ID if provided
  if (excludeId) {
    searchParameters.filter_by += searchParameters.filter_by
      ? ` && id:!=${excludeId}`
      : `id:!=${excludeId}`;
  }

  // Exclude adult tags if no filter_by is provided or if it's just 'status:publish'
  if ((!filter_by || filter_by === 'status:publish') && !query) {
    const nsfwFilter = getNSFWFilter();
    if (nsfwFilter) {
      if (searchParameters.filter_by) {
        searchParameters.filter_by += ` && ${nsfwFilter}`;
      } else {
        searchParameters.filter_by = nsfwFilter;
      }
    }
  }

  // Perform the search
  return searchClient
    .collections('products')
    .documents()
    .search(searchParameters)
    .then((response) => {
      return formatTypesenseToLaravelPagination(
        response,
        currentPage,
        limit || 12
      );
    })
    .catch((error) => {
      console.error('Error fetching data:', error);
      return { data: [] };
    });
}

export function searchFeaturedProducts(params: any): Promise<any> {
  const { limit = 8, page = 1 } = params;

  const nsfwFilter = getNSFWFilter();
  let filterBy = 'status:publish && is_custom_ai_model:false';

  if (nsfwFilter) {
    filterBy += ` && ${nsfwFilter}`;
  }

  const searchParameters: SearchParams | SearchParamsWithPreset = {
    q: '*',
    query_by: 'name,description',
    sort_by: 'orders_count:desc,_text_match:desc',
    // sort_by: 'featured:desc,_text_match:desc',
    per_page: limit,
    page: page,
    filter_by: filterBy,
  };

  return searchClient
    .collections('products')
    .documents()
    .search(searchParameters)
    .then((response) => {
      return formatTypesenseToLaravelPagination(response, page, limit);
    })
    .catch((error) => {
      console.error('Error fetching featured products:', error);
      return { data: [] };
    });
}

export function searchNewProducts(params: any): Promise<any> {
  const { limit = 8, page = 1 } = params;

  const nsfwFilter = getNSFWFilter();
  let filterBy = 'status:publish && is_custom_ai_model:false';

  if (nsfwFilter) {
    filterBy += ` && ${nsfwFilter}`;
  }

  const searchParameters: SearchParams | SearchParamsWithPreset = {
    q: '*',
    query_by: 'name,description',
    sort_by: 'created_at:desc,_text_match:desc',
    per_page: limit,
    page: page,
    filter_by: filterBy,
  };

  return searchClient
    .collections('products')
    .documents()
    .search(searchParameters)
    .then((response) => {
      return formatTypesenseToLaravelPagination(response, page, limit);
    })
    .catch((error) => {
      console.error('Error fetching trending products:', error);
      return { data: [] };
    });
}

export function searchPopularChatGPTPrompts(params: any): Promise<any> {
  const { limit = 12, page = 1 } = params;

  const nsfwFilter = getNSFWFilter();
  let filterBy = formatFilterParams({
    status: 'publish',
    aiModels: 'gpt',
    refineBy: 'prompt'
  });

  if (nsfwFilter) {
    filterBy += ` && ${nsfwFilter}`;
  }

  const searchParameters: SearchParams | SearchParamsWithPreset = {
    q: '*',
    query_by: 'name,description',
    sort_by: 'orders_count:desc,_text_match:desc',
    per_page: limit,
    page: page,
    filter_by: filterBy,
  };

  return searchClient
    .collections('products')
    .documents()
    .search(searchParameters)
    .then((response) => {
      return formatTypesenseToLaravelPagination(response, page, limit);
    })
    .catch((error) => {
      console.error('Error fetching popular ChatGPT prompts:', error);
      return { data: [] };
    });
}

export function searchArtIllustrationPrompts(params: any): Promise<any> {
  const { limit = 12, page = 1 } = params;

  const nsfwFilter = getNSFWFilter();
  let filterBy = formatFilterParams({
    status: 'publish',
    categories: 'art-and-design',
    refineBy: 'prompt'
  });

  if (nsfwFilter) {
    filterBy += ` && ${nsfwFilter}`;
  }

  const searchParameters: SearchParams | SearchParamsWithPreset = {
    q: '*',
    query_by: 'name,description',
    sort_by: 'orders_count:desc,_text_match:desc',
    per_page: limit,
    page: page,
    filter_by: filterBy,
  };

  return searchClient
    .collections('products')
    .documents()
    .search(searchParameters)
    .then((response) => {
      return formatTypesenseToLaravelPagination(response, page, limit);
    })
    .catch((error) => {
      console.error('Error fetching art & illustration prompts:', error);
      return { data: [] };
    });
}

export function searchLogoIconPrompts(params: any): Promise<any> {
  const { limit = 12, page = 1 } = params;

  const searchParameters: SearchParams | SearchParamsWithPreset = {
    q: '*',
    query_by: 'name,description',
    sort_by: 'orders_count:desc,_text_match:desc',
    per_page: limit,
    page: page,
    filter_by: formatFilterParams({
      status: 'publish',
      categories: 'business-and-marketing\\branding',
      refineBy: 'prompt'
    }),
  };

  return searchClient
    .collections('products')
    .documents()
    .search(searchParameters)
    .then((response) => {
      return formatTypesenseToLaravelPagination(response, page, limit);
    })
    .catch((error) => {
      console.error('Error fetching logo & icon prompts:', error);
      return { data: [] };
    });
}

export function searchPortraitPrompts(params: any): Promise<any> {
  const { limit = 12, page = 1 } = params;

  const nsfwFilter = getNSFWFilter();
  let filterBy = formatFilterParams({
    status: 'publish',
    categories: 'photography-and-video\\people',
    refineBy: 'prompt'
  });

  if (nsfwFilter) {
    filterBy += ` && ${nsfwFilter}`;
  }

  const searchParameters: SearchParams | SearchParamsWithPreset = {
    q: '*',
    query_by: 'name,description',
    sort_by: 'orders_count:desc,_text_match:desc',
    per_page: limit,
    page: page,
    filter_by: filterBy,
  };

  return searchClient
    .collections('products')
    .documents()
    .search(searchParameters)
    .then((response) => {
      return formatTypesenseToLaravelPagination(response, page, limit);
    })
    .catch((error) => {
      console.error('Error fetching portrait prompts:', error);
      return { data: [] };
    });
}
